/**
 * Lazy Sentry Loader
 *
 * Core error capture - loads after user interaction or 3s timeout.
 */

let sentryModule = null;
let loadingPromise = null;
let isInitialized = false;

// Pending user context — set before Sentry loads, flushed on init.
// Mirrors the pattern in src/utils/posthogIdentify.js so auth state isn't
// silently dropped when login completes before lazy-loaded Sentry is ready.
// `null` means "no pending state" (nothing to flush). Logout while pending
// just sets this back to null, which correctly leaves Sentry with no user.
let pendingUser = null;

function isNoisyError(event, hint) {
  const error = hint?.originalException;
  const message = error?.message || event.message || '';

  // Network / fetch errors (transient mobile connectivity)
  if (error?.name === 'TypeError' && /failed to fetch|network request failed|load failed/i.test(message)) {
    return true;
  }
  if (error?.name === 'TypeError' && /networkerror/i.test(message)) {
    return true;
  }

  // Chunk loading failures (lazy-load + deployment race conditions)
  if (/loading chunk|loading css chunk|dynamically imported module/i.test(message)) {
    return true;
  }

  // ResizeObserver loop errors (harmless browser noise)
  if (/resizeobserver loop/i.test(message)) {
    return true;
  }

  // AbortError (user navigated away)
  if (error?.name === 'AbortError') {
    return true;
  }

  return false;
}

/**
 * Initialize core Sentry functionality (error capture + tracing)
 * Does NOT include feedback integration - that's loaded on-demand
 * Safe to call multiple times - will only load/init once
 */
export const initSentryLazy = async () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn || dsn === 'your-sentry-dsn') {
    // Discard any queued user so it doesn't leak across the session
    pendingUser = null;
    return null;
  }

  // Don't report errors from local development — HMR causes false positives
  // (e.g. React context breaks during hot reload, producing "useAuth outside AuthProvider")
  if (import.meta.env.DEV) {
    pendingUser = null;
    return null;
  }

  if (sentryModule && isInitialized) {
    return sentryModule;
  }

  if (loadingPromise) {
    return loadingPromise;
  }

  loadingPromise = import('@sentry/react').then((Sentry) => {
    sentryModule = Sentry;

    if (!isInitialized) {
      // Enable tracing only when ?trace=1 is in URL (for debugging)
      // This reduces bundle overhead for normal users
      const enableTrace = new URLSearchParams(window.location.search).has('trace');

      // PostHog Sentry integration: adds a 'PostHog Person URL' tag to every
      // Sentry event so you can jump from a Sentry issue directly to the
      // user's PostHog profile. Requires PostHog to be loaded first (handled
      // by main.jsx sequential loadAnalytics).
      // sendExceptionsToPostHog=false: PostHog's own capture_exceptions is
      // disabled, so Sentry is the single source for errors. We explicitly
      // don't want Sentry forwarding events back to PostHog either — the
      // PostHog profile link is enough for cross-tool navigation.
      const posthogSentryIntegration = window.posthog?.sentryIntegration
        ? window.posthog.sentryIntegration({ sendExceptionsToPostHog: false })
        : null;

      Sentry.init({
        dsn,
        environment: import.meta.env.MODE,
        release: import.meta.env.VITE_BUILD_VERSION || 'dev',
        // 1.0 = capture 100% of errors. Sentry's default dedupeIntegration
        // (enabled automatically) handles consecutive-duplicate suppression,
        // and project-level quotas handle rate limiting at ingest.
        sampleRate: 1.0,
        tracesSampleRate: enableTrace ? 1.0 : 0,
        integrations: [
          ...(posthogSentryIntegration ? [posthogSentryIntegration] : []),
          ...(enableTrace ? [Sentry.browserTracingIntegration()] : []),
        ],
        // Filter known-noisy, non-actionable errors (network blips,
        // chunk-load races, ResizeObserver loop, AbortError).
        beforeSend: (event, hint) => (isNoisyError(event, hint) ? null : event),
      });

      window.Sentry = Sentry;
      isInitialized = true;

      // Flush any user context queued before Sentry finished loading.
      // Critical for early-session errors: without this, Sentry has no
      // user context until the next setSentryUser call (e.g., re-login).
      if (pendingUser) {
        Sentry.setUser(pendingUser);
        pendingUser = null;
      }
    }

    return Sentry;
  });

  return loadingPromise;
};

/**
 * Set Sentry user context. Safe to call before Sentry loads — the user
 * data is queued and flushed once Sentry initializes. Latest call wins.
 */
export const setSentryUser = (user) => {
  if (isInitialized && sentryModule) {
    sentryModule.setUser(user);
    pendingUser = null;
  } else {
    pendingUser = user;
  }
};

/**
 * Clear Sentry user context (e.g., on logout). Safe to call before Sentry
 * loads — the pending queue is cleared so no stale user is flushed.
 */
export const clearSentryUser = () => {
  pendingUser = null;
  if (isInitialized && sentryModule) {
    sentryModule.setUser(null);
  }
};

/**
 * Capture exception. Pre-init events (during the ~3s lazy-load window) fall
 * through to console.error — rare, and those same errors are already tracked
 * to PostHog via trackApiRequestFailed in api/base.js.
 */
export const captureException = (error, context) => {
  if (window.Sentry) {
    window.Sentry.captureException(error, context);
  } else {
    console.error('[Sentry not loaded]', error);
  }
};

/**
 * Capture message/warning
 */
export const captureMessage = (message, level = 'warning', extra = {}) => {
  if (window.Sentry) {
    window.Sentry.captureMessage(message, { level, extra });
  } else {
    console.warn('[Sentry not loaded]', message, extra);
  }
};

/**
 * Check if Sentry is loaded
 */
export const isSentryLoaded = () => isInitialized;
